// Record of order submissions that did not go through.
//
// Separate from the general API error log because a failed order carries
// something the error log does not: the cart itself. Keeping that snapshot
// means an order can be put back in front of the customer (or restored by
// whoever is looking into the failure) instead of being retyped.

const STORAGE_KEY = "failed-order-log";
const MAX_ENTRIES = 50;

function readRaw() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
}

function writeRaw(entries) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
    return true;
  } catch (e) {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(entries.slice(Math.floor(entries.length / 2)))
      );
      return true;
    } catch (e2) {
      return false;
    }
  }
}

/**
 * Record one failed order attempt, including the cart as it stood.
 *
 * @param {object} info
 * @param {string} info.reason        Human-readable failure reason
 * @param {string} [info.kind]        Failure class from the error log
 * @param {number|string} [info.status] HTTP status, if any
 * @param {string} [info.message]     Raw error message
 * @param {string} [info.uniqueOrderID]
 * @param {string|number} [info.custCode]
 * @param {string} [info.remarks]
 * @param {number|string} [info.approxValue]
 * @param {Array}  [info.items]       Cart snapshot (display shape)
 * @param {object} [info.payload]     What was actually posted
 */
export function logFailedOrder(info) {
  try {
    const opts = info || {};
    const items = Array.isArray(opts.items) ? opts.items : [];

    let user = {};
    try {
      user = JSON.parse(localStorage.getItem("user-info")) || {};
    } catch (e) {
      user = {};
    }

    const entry = {
      id: String(Date.now()) + "-" + Math.random().toString(36).slice(2, 8),
      time: new Date().toISOString(),
      reason: opts.reason || "Unknown reason",
      kind: opts.kind || "unknown",
      status: opts.status || "",
      message: opts.message || "",
      uniqueOrderID: opts.uniqueOrderID || "",
      custCode: opts.custCode || user.code || "",
      custName: user.name || user.userName || "",
      remarks: opts.remarks || "",
      approxValue: opts.approxValue === undefined ? "" : opts.approxValue,
      itemCount: items.length,
      totalQty: items.reduce((sum, it) => {
        const q = parseFloat(it && it.qty);
        return sum + (isNaN(q) ? 0 : q);
      }, 0),
      items: items,
      recovered: false,
    };

    const entries = readRaw();
    entries.push(entry);
    while (entries.length > MAX_ENTRIES) entries.shift();
    writeRaw(entries);

    try {
      window.dispatchEvent(
        new CustomEvent("order-failure-logged", { detail: entry })
      );
    } catch (e) {
      /* older browser — viewer picks it up on its next poll */
    }

    return entry;
  } catch (e) {
    return null;
  }
}

/** All failed order attempts, newest first. */
export function getFailedOrders() {
  return readRaw().slice().reverse();
}

/** Mark an attempt as dealt with (restored to a cart, or reordered). */
export function markFailedOrderRecovered(id, recovered) {
  try {
    const entries = readRaw().map((e) =>
      e.id === id
        ? { ...e, recovered: recovered === undefined ? true : !!recovered }
        : e
    );
    writeRaw(entries);
    return true;
  } catch (e) {
    return false;
  }
}

/** Drop a single attempt. */
export function removeFailedOrder(id) {
  try {
    writeRaw(readRaw().filter((e) => e.id !== id));
    return true;
  } catch (e) {
    return false;
  }
}

/** Wipe the whole record. */
export function clearFailedOrders() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Put a failed attempt's items back into the customer cart so the order can
 * be placed again. Returns the number of items restored.
 */
export function restoreFailedOrderToCart(entry, cartStorageKey) {
  try {
    if (!entry || !Array.isArray(entry.items) || entry.items.length === 0) {
      return 0;
    }
    const key = cartStorageKey || "selcted-Products";
    localStorage.setItem(key, JSON.stringify(entry.items));
    markFailedOrderRecovered(entry.id, true);
    return entry.items.length;
  } catch (e) {
    console.error("Could not restore order to cart", e);
    return 0;
  }
}

const CSV_COLUMNS = [
  "time",
  "reason",
  "kind",
  "status",
  "message",
  "uniqueOrderID",
  "custCode",
  "custName",
  "itemCount",
  "totalQty",
  "approxValue",
  "remarks",
  "recovered",
];

function csvCell(value) {
  const s = value === null || value === undefined ? "" : String(value);
  return '"' + s.replace(/"/g, '""') + '"';
}

/** Failed attempts as CSV, one row per order plus one row per line item. */
export function failedOrdersToCsv() {
  const rows = [CSV_COLUMNS.concat(["itemName", "itemCode", "itemQty"]).join(",")];
  getFailedOrders().forEach((entry) => {
    const base = CSV_COLUMNS.map((c) => csvCell(entry[c]));
    if (!entry.items || entry.items.length === 0) {
      rows.push(base.concat(['""', '""', '""']).join(","));
      return;
    }
    entry.items.forEach((it) => {
      rows.push(
        base
          .concat([
            csvCell(it.prd1 || it.prodName || ""),
            csvCell(it.productCode || ""),
            csvCell(it.qty || ""),
          ])
          .join(",")
      );
    });
  });
  return rows.join("\r\n");
}

/** Trigger a browser download of the failed-order record as CSV. */
export function downloadFailedOrdersCsv() {
  try {
    const blob = new Blob(["﻿" + failedOrdersToCsv()], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download =
      "failed-orders-" +
      new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-") +
      ".csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(link.href), 1000);
    return true;
  } catch (e) {
    console.error("Could not export failed orders", e);
    return false;
  }
}

const FailedOrderStore = {
  log: logFailedOrder,
  getAll: getFailedOrders,
  remove: removeFailedOrder,
  clear: clearFailedOrders,
  restore: restoreFailedOrderToCart,
  markRecovered: markFailedOrderRecovered,
  toCsv: failedOrdersToCsv,
  download: downloadFailedOrdersCsv,
  STORAGE_KEY,
};

export default FailedOrderStore;
